import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Loading, StatRow } from "@/components/Common";
import { EUR, EURc, pnlColor } from "@/lib/format";
import api from "@/lib/api";

export default function RiskMonitor() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["risk"], queryFn: () => api.get("/risk").then((r) => r.data) });
  const [limits, setLimits] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data?.limits) setLimits(data.limits); }, [data]);
  if (isLoading || !data) return <Loading />;

  const save = async () => {
    setSaving(true);
    try { await api.put("/risk/limits", limits); toast.success("Limiti aggiornati"); refetch(); }
    catch { toast.error("Errore"); } finally { setSaving(false); }
  };

  const metrics = [
    { l: "Current Drawdown", v: EUR(data.current_drawdown), tone: data.current_drawdown > 0 ? "loss" : undefined },
    { l: "Maximum Drawdown", v: `${EUR(data.max_drawdown)} · ${data.max_drawdown_pct}%`, tone: "loss" },
    { l: "Daily P&L", v: EURc(data.daily_pnl), tone: data.daily_pnl >= 0 ? "profit" : "loss" },
    { l: "Consecutive Losses", v: data.consecutive_losses, tone: data.consecutive_losses >= 3 ? "loss" : undefined },
    { l: "Consecutive Wins", v: data.consecutive_wins, tone: "profit" },
    { l: "Largest Loss", v: EURc(data.largest_loss), tone: "loss" },
    { l: "Avg Risk / Trade", v: EUR(data.avg_risk_per_trade) },
    { l: "Average R", v: data.avg_r.toFixed(2), tone: data.avg_r >= 0 ? "profit" : "loss" },
    { l: "Trading Frequency", v: `${data.trading_frequency} / giorno` },
  ];

  const limitFields = [
    { k: "daily_loss_limit", l: "Daily Loss Limit (€)" },
    { k: "max_drawdown", l: "Maximum Drawdown (€)" },
    { k: "max_trades_per_day", l: "Max Trades / Day" },
    { k: "max_consecutive_losses", l: "Max Consecutive Losses" },
  ];

  return (
    <div data-testid="risk-page">
      <PageHeader title="Risk Monitor" subtitle="Monitoraggio del rischio — solo informativo, nessuna azione automatica" />

      {(data.breaches || []).length > 0 && (
        <div className="mb-6 space-y-2" data-testid="risk-breaches">
          {data.breaches.map((b, i) => (
            <div key={i} className="flex items-center gap-3 rounded-lg border border-loss/30 bg-loss/10 px-4 py-3">
              <AlertTriangle size={16} className="text-loss" />
              <span className="font-sans text-sm text-loss">{b.message}</span>
            </div>
          ))}
        </div>
      )}
      {(data.breaches || []).length === 0 && (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-profit/25 bg-profit/5 px-4 py-3">
          <ShieldCheck size={16} className="text-profit" />
          <span className="font-sans text-sm text-profit">Tutti i limiti di rischio sono rispettati</span>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="dlx-card p-5 sm:p-6 lg:col-span-2">
          <div className="mb-4 font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Metriche di Rischio</div>
          <div className="grid gap-x-8 sm:grid-cols-2">
            {metrics.map((m) => <StatRow key={m.l} label={m.l} value={m.v} tone={m.tone} />)}
          </div>
        </div>

        <div className="dlx-card p-5 sm:p-6" data-testid="risk-limits-form">
          <div className="mb-4 font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Limiti Personali</div>
          <div className="space-y-4">
            {limitFields.map((f) => (
              <div key={f.k}>
                <label className="mb-1.5 block font-sans text-[11px] text-zinc-400">{f.l}</label>
                <input type="number" value={limits[f.k] ?? ""} data-testid={`limit-${f.k}`}
                  onChange={(e) => setLimits({ ...limits, [f.k]: e.target.value === "" ? null : Number(e.target.value) })}
                  className="w-full rounded-lg border border-white/10 bg-[#141418] px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-gold/40" />
              </div>
            ))}
          </div>
          <button onClick={save} disabled={saving} data-testid="save-limits"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg py-2.5 font-sans text-sm font-semibold text-black transition-all hover:brightness-110 active:scale-95"
            style={{ background: "linear-gradient(135deg,#BF953F,#FCF6BA 45%,#AA771C)" }}>
            <Save size={15} /> {saving ? "Salvataggio…" : "Salva Limiti"}
          </button>
        </div>
      </div>
    </div>
  );
}
